import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { FloatLabel } from 'primeng/floatlabel';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { CompanyService } from '../../../../services/remote/company.service';
import { LicenseService } from '../../../../services/remote/license.service';
import { NotificationService } from '../../../../services/shared/notification.service';
import { LicenseDetailsDialogComponent } from '../license-details-dialog/license-details-dialog.component';
import { QueryResult } from '../license/types/licenseTypes';
import { LicenseStatus } from '../../../../types/license';

@Component({
  selector: 'cognos-license-list',
  imports: [
    LicenseDetailsDialogComponent,
    ButtonModule,
    TableModule,
    TagModule,
    ConfirmDialog,
    FloatLabel,
    InputTextModule,
    InputIcon,
    IconField,
  ],
  providers: [ConfirmationService],
  templateUrl: './license-list.component.html',
  styleUrl: './license-list.component.scss',
})
export class LicenseListComponent {
  @Input({ required: true }) companyName: string = '';
  @Input({ required: true }) licenses: any[] = [];

  @Output() onEditLicense = new EventEmitter<any>();

  public statusOptions = this.licenseService.statusOptions$;

  public selectedLicense: { id: string; [key: string]: any } | undefined =
    undefined;

  public licenseDetails: { id: string; [key: string]: any } | undefined =
    undefined;
  public licenseSettings: any;
  public isLicenseDetailVisible = false;

  constructor(
    public licenseService: LicenseService,
    private _companyService: CompanyService,
    private _notificationService: NotificationService,
    private _confirmationService: ConfirmationService,
  ) {}

  public onGlobalFilter(event: Event, table: any) {
    const input = event.target as HTMLInputElement;
    table.filterGlobal(input.value, 'contains');
  }

  public async handleRowClick() {
    if (!this.selectedLicense) return;

    const results = await Promise.all([
      this.licenseService.fetchLicenseSettings(this.selectedLicense.id),
      this.licenseService.fetchLicenseVersion(this.selectedLicense.id),
      this.licenseService.fetchRemoteSourcesList(this.selectedLicense.id),
    ]);

    // Resets the selected row at the end.
    setTimeout(() => {
      this.selectedLicense = undefined;
    });

    if (results.some((res) => res !== 'ok')) {
      this._notificationService.showErrorNotification(
        'Failed to load data. Please try again.',
      );
      return;
    }

    this.licenseDetails = structuredClone(this.selectedLicense);
    this.licenseSettings = structuredClone(
      this.licenseService.licenseSettings$(),
    );
    this.isLicenseDetailVisible = true;
  }

  public handleEditLicenseClick(license: any) {
    this.onEditLicense.emit(license);
  }

  public handleDeleteLicenseClick(event: Event, licenseId: string) {
    this._confirmDeletion(event, licenseId);
  }

  public onLicenseUpdated(update: { id: string; status: LicenseStatus }) {
    if (!update?.id) return;

    const index = this.licenses.findIndex(
      (license) => license.id === update.id,
    );
    if (index !== -1) {
      this.licenses[index] = {
        ...this.licenses[index],
        status: update.status,
      };
    }

    if (this.licenseDetails?.id === update.id) {
      this.licenseDetails = {
        ...this.licenseDetails,
        status: update.status,
      };
    }
  }

  private _confirmDeletion(event: Event, licenseId: string) {
    this._confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Do you want to delete this license?',
      header: 'Delete Confirmation',
      icon: 'pi pi-trash',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        raised: true,
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger',
        raised: true,
      },
      accept: async () => {
        const result = await this.licenseService.deleteLicense(licenseId);
        if (result === QueryResult.ok) {
          this._companyService.fetchCompanies();
          this._notificationService.showSuccessNotification(
            'License was succesfully deleted.',
          );
        } else {
          this._notificationService.showErrorNotification(
            'Deletion failed. Please try again later.',
          );
        }
      },
      reject: () => {},
    });
  }
}
